import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../../accounting/accounting.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useQueryClient } from '@tanstack/react-query';
import { api, runWithAuditReason } from '../../../api/client';
import { useCountries, useSaveCountry, useDeleteCountry, errMessage, type Country } from './systemMasters';
import { CountryStatesPanel } from './CountryStatesPanel';
import type { StagedState } from './city';

interface Props { onHome?: () => void; }

/**
 * Settings ▸ System Management ▸ Country — CRUD over the legacy dbo.CustomCountry
 * master, mirroring the XAF `CustomCountry_ListView` / detail: a grid of
 * EnglishLongName + EnglishShortName with two row pencils (inline-edit +
 * open-detail), New / Edit / Delete / Refresh / Export, and an Audit trail.
 */
export function CountryPage({ onHome }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = useCountries();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveCountry();
  const del = useDeleteCountry();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [editing, setEditing] = useState<Country | null | undefined>(undefined);
  const [longName, setLongName] = useState('');
  const [shortName, setShortName] = useState('');
  const [flag, setFlag] = useState<string | null>(null);   // NationalFlag as base64 (no data-URI prefix)
  const flagInputRef = useRef<HTMLInputElement>(null);     // hidden file picker behind the edit pencil
  const [err, setErr] = useState('');
  // States staged in memory while creating a brand-new country (LIMS parity) —
  // the whole Country→State→City→Area chain commits when the country is saved.
  const [stagedStates, setStagedStates] = useState<StagedState[]>([]);
  // Inline row-edit (legacy 1st pencil): EnglishLongName + EnglishShortName in place.
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [draftLong, setDraftLong] = useState('');
  const [draftShort, setDraftShort] = useState('');
  const [savingInline, setSavingInline] = useState(false);
  // Maximize fills the content area (the grid card) like the LIMS full-page detail.
  const [maxed, setMaxed] = useState(false);  // open RIGHT-SIZED (not full-screen); user can maximize via the header button
  const cardRef = useRef<HTMLDivElement>(null);
  const [maxRect, setMaxRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!maxed) { setMaxRect(null); return; }
    const measure = () => {
      const r = cardRef.current?.getBoundingClientRect();
      if (r) setMaxRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [maxed]);
  const maxStyle = maxed && maxRect
    ? { position: 'fixed' as const, top: maxRect.top, left: maxRect.left, width: maxRect.width,
        height: maxRect.height, maxWidth: 'none' as const, maxHeight: 'none' as const, margin: 0 }
    : undefined;

  const openNew = () => { setEditing(null); setLongName(''); setShortName(''); setFlag(null); setErr(''); setMaxed(false); setStagedStates([]); };
  const openEdit = (r: Country) => { setEditing(r); setLongName(r.longName ?? ''); setShortName(r.shortName ?? ''); setFlag(r.nationalFlag ?? null); setErr(''); setMaxed(false); setStagedStates([]); };
  // Read a chosen image file into base64 (strip the data-URI prefix the API doesn't want).
  const onFlagFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setFlag(String(reader.result).split(',')[1] ?? null);
    reader.readAsDataURL(file);
  };
  const editSelected = () => {
    if (selectedIds.size !== 1) { notify.warning('Select exactly one country to edit.'); return; }
    const r = rows.find((x) => x.id === String([...selectedIds][0])); if (r) openEdit(r);
  };
  const close = () => { setEditing(undefined); setMaxed(false); setStagedStates([]); };

  const startInline = (r: Country) => { setInlineId(r.id); setDraftLong(r.longName ?? ''); setDraftShort(r.shortName ?? ''); };
  const cancelInline = () => { setInlineId(null); setDraftLong(''); setDraftShort(''); };
  const saveInline = async (r: Country) => {
    if (!draftLong.trim()) { notify.warning('English Long Name must not be empty.'); return; }
    // Reason-for-change gate (standing rule 11) — only when ReasonRequired is ON.
    const reason = await gateReason('Country', 'edit');
    if (reason === null) return;
    setSavingInline(true);
    try {
      await runWithAuditReason(reason, () => save.mutateAsync({
        id: r.id, longName: draftLong.trim(), shortName: draftShort.trim() || null,
        chineseLongName: r.chineseLongName ?? null, chineseShortName: r.chineseShortName ?? null,
        nationalFlag: r.nationalFlag ?? null,   // preserve the flag on an inline rename
      }));
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      cancelInline();
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSavingInline(false); }
  };

  /** Save (create/update) then act per the button: stay open, close, or reset to New (LIMS detail parity). */
  const doSave = async (after: 'stay' | 'close' | 'new') => {
    if (!longName.trim()) { setErr('English Long Name must not be empty.'); return; }
    setErr('');
    const wasNew = !editing?.id;
    // Reason-for-change gate (standing rule 11) — only on an EDIT; Create is exempt.
    let reason = '';
    if (!wasNew) {
      const r = await gateReason('Country', 'edit');
      if (r === null) return;
      reason = r;
    }
    let newId: string | undefined;
    await runSave({
      save: () => runWithAuditReason(reason, async () => {
        const res = await save.mutateAsync({
          id: editing?.id, longName: longName.trim(), shortName: shortName.trim() || null,
          chineseLongName: editing?.chineseLongName ?? null, chineseShortName: editing?.chineseShortName ?? null,
          nationalFlag: flag,
        });
        newId = editing?.id ?? (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
        // Commit states staged on a brand-new country (create new / re-assign linked),
        // then each state's staged cities, then each city's staged areas — the full
        // Country → State → City → Area chain (LIMS nested-save parity).
        if (wasNew && newId && stagedStates.length) {
          for (const ss of stagedStates) {
            let stateId = ss.stateId;
            if (stateId) {
              await api.put(`/api/v1/system/states/${stateId}`, { longName: ss.name, shortName: ss.shortName ?? null, countryId: newId });
            } else {
              const sres = await api.post('/api/v1/system/states', { longName: ss.name, shortName: ss.shortName ?? null, countryId: newId });
              stateId = (sres as { data?: { data?: { id?: string } } })?.data?.data?.id;
            }
            if (stateId && ss.cities?.length) {
              for (const sc of ss.cities) {
                let cityId = sc.cityId;
                if (cityId) {
                  await api.put(`/api/v1/cities/${cityId}`, { cityName: sc.name, stateId });
                } else {
                  const cres = await api.post('/api/v1/cities', { cityName: sc.name, stateId });
                  cityId = (cres as { data?: { data?: { id?: string } } })?.data?.data?.id;
                }
                if (cityId && sc.areas?.length) {
                  for (const a of sc.areas) {
                    if (a.areaId) await api.post(`/api/v1/cities/${cityId}/areas/link`, { areaId: a.areaId });
                    else await api.post(`/api/v1/cities/${cityId}/areas`, { areaName: a.name });
                  }
                }
              }
            }
          }
          // The staged states/cities/areas were committed via raw api.post AFTER the
          // country-save invalidation fired — refresh those caches so the grids show
          // the committed rows instead of the stale (empty) list.
          await qc.invalidateQueries({ queryKey: ['sm-states-full'] });
          await qc.invalidateQueries({ queryKey: ['sm-cities'] });
          await qc.invalidateQueries({ queryKey: ['sm-city-areas'] });
        }
      }),
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (after === 'close') { close(); return; }
        if (after === 'new') { openNew(); return; }
        if (wasNew && newId) setEditing({ id: newId, longName: longName.trim(), shortName: shortName.trim() || null, chineseLongName: null, chineseShortName: null, nationalFlag: flag });
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one country to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} countr${selectedIds.size === 1 ? 'y' : 'ies'}?` })) return;
    const reason = await gateReason('Country', 'delete');
    if (reason === null) return;
    try { await runWithAuditReason(reason, async () => { for (const id of selectedIds) await del.mutateAsync(String(id)); }); setSelectedIds(new Set()); await alert({ title: 'Success', message: 'Deleted.', tone: 'success' }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => [r.longName, r.shortName].some((v) => (v ?? '').toLowerCase().includes(q))) : rows;
  }, [rows, search]);

  const cmp = (get: (r: Country) => string | null) => (a: Country, b: Country) =>
    (get(a) ?? '').localeCompare(get(b) ?? '', undefined, { sensitivity: 'base' });

  const columns: AppColumn<Country>[] = [
    {
      // 1st pencil — inline edit in place (Save / Cancel).
      field: 'inlineedit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) =>
        inlineId === r.id ? (
          <span className="pts-rowedit" onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={savingInline} onClick={() => void saveInline(r)}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={savingInline} onClick={cancelInline}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Inline edit" onClick={(e) => { e.stopPropagation(); startInline(r); }}><i className="ti ti-edit" /></button>
        ),
    },
    {
      // 2nd pencil — open the full edit dialog; hidden while inline-editing.
      field: 'editopen', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) =>
        inlineId === r.id ? null : (
          <button className="pts-icon-btn edit" title="Open full edit screen" onClick={(e) => { e.stopPropagation(); openEdit(r); }}><i className="ti ti-external-link" /></button>
        ),
    },
    {
      field: 'longName', header: 'EnglishLongName', minWidth: 300, text: (r) => r.longName ?? '', compare: cmp((r) => r.longName),
      render: (r) =>
        inlineId === r.id ? (
          <input className="pts-input" autoFocus value={draftLong} disabled={savingInline}
            onClick={(e) => e.stopPropagation()} onChange={(e) => setDraftLong(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(r); if (e.key === 'Escape') cancelInline(); }} />
        ) : (<span>{r.longName ?? ''}</span>),
    },
    {
      field: 'shortName', header: 'EnglishShortName', minWidth: 300, sortable: false, text: (r) => r.shortName ?? '',
      render: (r) =>
        inlineId === r.id ? (
          <input className="pts-input" value={draftShort} disabled={savingInline}
            onClick={(e) => e.stopPropagation()} onChange={(e) => setDraftShort(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(r); if (e.key === 'Escape') cancelInline(); }} />
        ) : (<span>{r.shortName ?? ''}</span>),
    },
    {
      // NationalFlag (legacy column) — the stored flag image, blank when none (LIMS parity).
      field: 'nationalFlag', header: 'NationalFlag', minWidth: 300, sortable: false, filterable: false,
      text: () => '',
      render: (r) => (r.nationalFlag ? <img className="cty-flag" src={`data:image;base64,${r.nationalFlag}`} alt="" /> : null),
    },
  ];

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Country</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        <FormTitle pageId="sm-country" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Country" />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        <AppToolbar
          onNew={openNew}
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onDelete={() => void removeSelected()} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="Country" headers={['EnglishLongName', 'EnglishShortName']} rows={visible.map((r) => [r.longName ?? '', r.shortName ?? ''])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (standard) — never inside the card. */}
        <div className="list-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card" ref={cardRef}>
          <AppDataGrid<Country> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={inlineId ?? editing?.id ?? null}
            initialSort={{ key: 'longName', dir: 'asc' }} loading={isFetching} emptyText="No countries found." />
        </div>
      </div>

      {editing !== undefined && (
        <div className="acc-overlay" onMouseDown={close}>
          <div className={`acc-dialog acc-dialog-md${maxed ? ' acc-dialog-max' : ''}`} style={maxStyle} onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr">
              <span>{editing ? `Country - ${editing.longName ?? ''}` : 'Country'}</span>
              <span className="acc-dialog-acts">
                <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`}
                  title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
                <i className="ti ti-x acc-dialog-x" onClick={close} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">English Long Name<span className="req">*</span></label>
                <div className="acc-fh-control">
                  <input className="acc-input" autoFocus value={longName} onChange={(e) => { setLongName(e.target.value); if (err) setErr(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void doSave('stay'); }} />
                  {err && <div className="acc-err">{err}</div>}
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">English Short Name</label>
                <div className="acc-fh-control">
                  <input className="acc-input" value={shortName} onChange={(e) => setShortName(e.target.value)} />
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">National Flag</label>
                <div className="acc-fh-control">
                  {/* No image → file textbox + Browse…; after upload → preview + edit/erase (XAF ImageEditor). */}
                  {flag ? (
                    <>
                      <img className="cty-flag-lg" src={`data:image;base64,${flag}`} alt="National flag preview" />
                      <div className="cty-flag-acts">
                        <button className="cty-flag-btn" title="Edit / choose image" onClick={() => flagInputRef.current?.click()}><i className="ti ti-pencil" /></button>
                        <button className="cty-flag-btn" title="Erase" onClick={() => setFlag(null)}><i className="ti ti-eraser" /></button>
                      </div>
                    </>
                  ) : (
                    <div className="acc-lookup">
                      <input className="acc-input" readOnly value="" placeholder="" onClick={() => flagInputRef.current?.click()} />
                      <button className="acc-browse-btn" onClick={() => flagInputRef.current?.click()}>Browse…</button>
                    </div>
                  )}
                  <input ref={flagInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={(e) => { onFlagFile(e.target.files?.[0]); e.target.value = ''; }} />
                </div>
              </div>
              {/* State child collection — Country → State (one-to-many). Mirrors
                  CustomCountry_DetailView ▸ State; activates once the country is saved. */}
              <CountryStatesPanel countryId={editing?.id} staged={stagedStates} onStaged={setStagedStates} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void doSave('stay')} disabled={save.isPending}><span className="tb-i">💾</span> Save</button>
              <button className="ca-btn" onClick={() => void doSave('close')} disabled={save.isPending}><span className="tb-i">💾</span> Save and Close</button>
              <button className="ca-btn" onClick={() => void doSave('new')} disabled={save.isPending}><span className="tb-i">💾</span> Save and New</button>
              <button className="ca-btn" onClick={close}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
